import React, {useState, useEffect} from "react";
import {Formik, Form, Field} from "formik";
import {LabelBlueBold} from "../../../Layout/Shared/LabelBlueBold";
import {Autocomplete} from "../../../Layout/Shared/Autocomplete";
import {RightToggler} from "../../../Layout/Shared/RightToggler/index";
import {BusinessUpdateAppointmentSchema} from "../../../../schemas/Calendar/Business/appointment";
import ErrorField from "../../../../components/Layout/Shared/ErrorField";
import {
  getOnlyDate,
  getOnlyTime,
  getTodayDateString,
  getAvailableTimesForDate
} from "../../../../utils/date";
import {updateAppointment} from "../../../../data/Calendar/Business/updateAppointment";
import {list as getClients} from "../../../../data/Client/Business/get-list";
import moment from "moment";

export const EditAppointment = ({close, appointment, onSuccess}) => {
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  const getValidTime = (dateValue) => {
    const dateStr = getOnlyDate(dateValue);
    const availableTimes = getAvailableTimesForDate(dateStr);
    const requestedTime = getOnlyTime(dateValue);
    return availableTimes.includes(requestedTime) ? requestedTime : availableTimes[0];
  };

  const initialValues = {
    id: appointment.id,
    clientEmail: appointment.clientEmail,
    firstName: appointment.firstName,
    lastName: appointment.lastName,
    phone: appointment.phone || "",
    appointmentDate: getOnlyDate(appointment.date),
    appointmentTime: getValidTime(appointment.date),
    reminder: appointment.reminder,
    pushNotification: appointment.pushNotification,
    notes: appointment.notes || "",
  };

  useEffect(() => {
    const fetchClients = async () => {
      const {hasSuccess, data} = await getClients();
      if (hasSuccess && Array.isArray(data)) {
        setClients(data);
      }
      setClientsLoading(false);
    };
    fetchClients();
  }, []);

  const clientOptions = clients.map(client => ({
    id: client.id || client._id,
    text: client.email,
    firstName: client.firstName,
    lastName: client.lastName,
    phone: client.phone || ""
  }));

  const handleClientSelect = (option, setFieldValue, validateForm) => {
    if (option && option.id) {
      setFieldValue("clientEmail", option.text, false);
      setFieldValue("firstName", option.firstName || "", false);
      setFieldValue("lastName", option.lastName || "", false);
      setFieldValue("phone", option.phone || "", false);
      // Validate after React commits all state updates
      setTimeout(() => validateForm(), 0);
    }
  };

  const onSubmit = async (values) => {
    const {
      clientEmail,
      firstName,
      lastName,
      phone,
      appointmentDate,
      appointmentTime,
      reminder,
      pushNotification,
      notes,
    } = values;
    const {hasSuccess} = await updateAppointment({
      id: initialValues.id,
      clientEmail,
      firstName,
      lastName,
      phone,
      date: moment(
        `${appointmentDate} ${appointmentTime}`,
        "YYYY-MM-DD HH:mm A"
      ).format(),
      reminder,
      pushNotification,
      notes,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    hasSuccess && onSuccess();
    close();
  };

  return (
    <>
      <RightToggler title="EDIT APPOINTMENT" size="500px" close={close}>
        <Formik
          validationSchema={BusinessUpdateAppointmentSchema}
          validateOnBlur={false}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          {({errors, setFieldValue, values, validateForm, isSubmitting}) => (
            <Form>
              <div className="ps-5 pe-5 pt-3 pb-5">
                <div>
                  <LabelBlueBold content="Client Email" />
                  <Autocomplete
                    name="clientEmail"
                    className="custom-input bg-transparent"
                    placeholder="Search or enter client email"
                    options={clientOptions}
                    defaultValue={values.clientEmail}
                    onSelect={option => handleClientSelect(option, setFieldValue, validateForm)}
                    onInputChange={value => setFieldValue("clientEmail", value)}
                    loading={clientsLoading}
                  />
                  <ErrorField message={errors.clientEmail} />
                </div>
                <div className="mt-3">
                  <LabelBlueBold content="Client Phone" />
                  <Field
                    name="phone"
                    type="tel"
                    className="custom-input bg-transparent"
                    placeholder="Phone Number"
                  />
                </div>
                <div className="row mt-4">
                  <div className="col-6">
                    <LabelBlueBold content="First Name" />
                    <Field
                      name="firstName"
                      className="custom-input bg-transparent"
                      placeholder="First Name"
                    />
                    <ErrorField message={errors.firstName} />
                  </div>
                  <div className="col-6">
                    <LabelBlueBold content="Last Name" />
                    <Field
                      name="lastName"
                      className="custom-input bg-transparent"
                      placeholder="Last Name"
                    />
                    <ErrorField message={errors.lastName} />
                  </div>
                </div>
              </div>
              <div className="clearfix border-top"></div>
              <div className="ps-5 pe-5  pt-4">
                <div className="row">
                  <div className="col-6">
                    <LabelBlueBold content="Apt. Date" />
                    <Field name="appointmentDate">
                      {({field: {value}, form: {setFieldValue, values}}) => (
                        <input
                          type="date"
                          className="custom-input bg-transparent"
                          placeholder="Choose Date"
                          value={value}
                          min={getTodayDateString()}
                          onChange={({target}) => {
                            const newDate = target.value;
                            setFieldValue("appointmentDate", newDate, false);
                            // Update time if current time is not valid for new date
                            const availableTimes = getAvailableTimesForDate(newDate);
                            if (!availableTimes.includes(values.appointmentTime)) {
                              setFieldValue("appointmentTime", availableTimes[0], true);
                            } else {
                              setFieldValue("appointmentTime", values.appointmentTime, true);
                            }
                          }}
                        />
                      )}
                    </Field>
                    <ErrorField message={errors.appointmentDate} />
                  </div>
                  <div className="col-6">
                    <LabelBlueBold content="Apt. Time" />
                    <Field name="appointmentTime">
                      {({field: {value}, form: {setFieldValue, values}}) => {
                        const availableTimes = getAvailableTimesForDate(
                          values.appointmentDate
                        );
                        return (
                          <select
                            className="primary-select bg-transparent"
                            value={value}
                            onChange={({target}) =>
                              setFieldValue("appointmentTime", target.value, true)
                            }
                          >
                            {availableTimes.map((time) => (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            ))}
                          </select>
                        );
                      }}
                    </Field>
                    <ErrorField message={errors.appointmentTime} />
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col-6">
                    <LabelBlueBold content="Push Notification" />
                    <Field name="pushNotification">
                      {({ field: { value }, form: { setFieldValue } }) => (
                        <select
                          className="primary-select bg-transparent"
                          value={value}
                          onChange={({ target }) =>
                            setFieldValue("pushNotification", target.value === "true")
                          }
                        >
                          <option value="true">Enabled</option>
                          <option value="false">Disabled</option>
                        </select>
                      )}
                    </Field>
                  </div>
                  <div className="col-6">
                    <LabelBlueBold content="Reminder Time" />
                    <Field name="reminder">
                      {({ field: { value }, form: { setFieldValue, values } }) => (
                        <select
                          className="primary-select bg-transparent"
                          value={value}
                          disabled={!values.pushNotification}
                          onChange={({ target }) =>
                            setFieldValue("reminder", target.value)
                          }
                        >
                          <option value={15}>15 Minutes Earlier Apt.</option>
                          <option value={30}>30 Minutes Earlier Apt.</option>
                          <option value={60}>60 Minutes Earlier Apt.</option>
                        </select>
                      )}
                    </Field>
                  </div>
                </div>
              </div>
              <div className="mt-5 clearfix border-top"></div>
              <div className="ps-5 pe-5  pt-4 pb-5">
                <div>
                  <LabelBlueBold content="Notes" />
                  <Field
                    as="textarea"
                    name="notes"
                    className="custom-input bg-transparent"
                    placeholder="Add notes..."
                    style={{ minHeight: "150px", resize: "vertical" }}
                  />
                </div>
              </div>
              <div className="toggle-footer">
                <div className="d-flex justify-content-between">
                  <button
                    type="submit"
                    className="btn-blue btn-small w-100"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Updating..." : "Update Appointment"}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </RightToggler>
    </>
  );
};
